import React, { Component, useCallback, useEffect, useMemo, useState } from "react";
import {
  EmailCheckResponse,
  InviteStatuses,
  RenewedTeacherInfo,
  Student,
  TeacherClass,
  TeacherInfo,
} from "../../../../../store/onboarding/types";
import { Button, Col, Form, Modal, Row, Spinner } from "react-bootstrap";
import CreateClassModal from "../../../../../containers/onboarding/second-step/teacher-details/CreateClassModal";
import UploadStudentsRosterToTeacherModal from "../../../../../containers/onboarding/second-step/students/UploadStudentsRosterToTeacherModal";
import { ApplicationState } from "../../../../../store";
import { bindActionCreators, Dispatch } from "redux";
import {
  assignStudentsToTeacher,
  changeSelectedTeacher,
  checkEmailExist,
  getClassesAssignedToTeacher,
  getStudentsAssignedToTeacher,
  getTeachers,
  hideTeacherDetailsModal,
  setShowDisabledUsers,
  updateTeacher,
  changeUserStatus,
} from "../../../../../store/onboarding/actions";
import { connect, useDispatch, useSelector } from "react-redux";
import TeacherTab from "./TeacherTab";
import TeacherInfoForm from "./TeacherInfoForm";
import { UserAssignmentRole, UserAssignmentRoleDisplayedNames } from "../../../../../store/auth/types";
import EducatorsManagementTab from "./EducatorsManagementTab";
import { useLoading } from "../../../../../utils/hooks/useLoading";
import { getFullName } from "../../../../../utils/NamesUtils";
import useUserRole from "../../../../../utils/hooks/useUserRole";
import { toastr } from "react-redux-toastr";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import InfoButton from "../../first-step/InfoButton";
import { faLock } from "@fortawesome/free-solid-svg-icons";

type PropsFromState = {
  selectedTeacher?: TeacherInfo;
  teachersClasses: Array<TeacherClass>;
  teachersStudents: Array<Student>;
  isLoading: {
    assignClassToTeacher: boolean;
    updateTeacherInfo: boolean;
    updateTeacherClass: boolean;
    assignStudentsToTeacher: boolean;
  };
  errors?: {
    assignClassToTeacher?: string;
    getClassesAssignedToTeacher?: string;
    getStudentsAssignedToTeacher?: string;
    updateTeacherInfo?: string;
    updateTeacherClass?: string;
    assignStudentsToTeacher?: string;
  };
  showModal: boolean;
};

type DispatchProps = {
  getClassesAssignedToTeacher: (id: number) => any;
  getStudentsAssignedToTeacher: (id: number) => any;
  assignStudentsToTeacher: (id: number, students: Array<Student>) => any;
  getTeachers: (withDisabled?: boolean) => any;
  updateTeacher: (id: number, updatedInfo: RenewedTeacherInfo) => any;
  changeSelectedTeacher: (selectedTeacher?: TeacherInfo) => any;
  hideTeacherDetailsModal: () => any;
  setShowDisabledUsers: (showDisabledUsers: boolean) => any;
  checkEmailExist: (inviteEmails:Array<any>) => any;
  changeUserStatus: (userId: number, isActive: boolean) => any;
};
type OwnProps = {
  fromManageUsers?: boolean;
}
type Props = PropsFromState & DispatchProps & OwnProps;

const TeacherDetailsModal: React.FC<Props> = (props) => {
  const { showModal, selectedTeacher, fromManageUsers } = props;
  const [isFieldValidated, setIsFieldValidated] = useState<boolean>(true);

  const reloadData = () => {
    if (selectedTeacher) {
      props.getClassesAssignedToTeacher(selectedTeacher.user.id);
      props.getStudentsAssignedToTeacher(selectedTeacher.user.id);
    }
  };

  useEffect(() => {
    reloadData();
    fromManageUsers && setIsFieldValidated(true);
  }, [selectedTeacher]);

  useLoading(
    props.isLoading.assignClassToTeacher,
    props.errors?.assignClassToTeacher,
    reloadData
  );
  useLoading(
    props.isLoading.updateTeacherClass,
    props.errors?.updateTeacherClass,
    reloadData
  );

  const showDisabledUsers = useSelector<ApplicationState, boolean | undefined>(
    (s) => s.onboarding.showDisabledUsers
  );
  const onUpdateInfoSuccess = useCallback(() => {
    props.changeSelectedTeacher(undefined);
    if(!fromManageUsers) { //RC-1457
      props.getTeachers();
    }
  }, []);
  useLoading(
    props.isLoading.updateTeacherInfo,
    props.errors?.updateTeacherInfo,
    onUpdateInfoSuccess
  );


  const handleHide = (isUpdated = false) => {
    if (!props.isLoading.updateTeacherInfo) {
      props.changeSelectedTeacher(undefined);
      if(isUpdated){
        props.getTeachers(showDisabledUsers);
      }
    }
  };

  const handleSubmit = (renewedTeacherInfo: RenewedTeacherInfo, activeStatus: boolean) => (
    event: React.FormEvent
  ) => {
    event.preventDefault();
    const selectedTeacher = props.selectedTeacher;
    if(renewedTeacherInfo.email && (selectedTeacher?.user.email !== renewedTeacherInfo.email)) {
      props.checkEmailExist([renewedTeacherInfo.email])
        .then((result:EmailCheckResponse) => {
          if(!result.emails_already_exist.length) {
              if (selectedTeacher) {
                props.updateTeacher(selectedTeacher.user.id, renewedTeacherInfo)
                .then(() => {
                  props.changeUserStatus(selectedTeacher.user.id, activeStatus).then(() => {
                    if(fromManageUsers) { //RC-1457
                      props.setShowDisabledUsers(showDisabledUsers!);
                      //props.getTeachers(showDisabledUsers);
                      handleHide(true);
                    }
                  },
                  (error: string) => {
                    toastr.error("Error", error);
                  });
                });
              }
          } else {
            toastr.error("Alert!!", 'The email already exist in the system. Please use a different email to invite this user(s).')
            return;
          } 
        },
        (err: any) =>  toastr.error("Error", err)
      )
    } else {
      if (selectedTeacher) {
        delete renewedTeacherInfo.email
        props.updateTeacher(selectedTeacher.user.id, renewedTeacherInfo)
        .then(() => {
          props.changeUserStatus(selectedTeacher.user.id, activeStatus).then(() => {
            if(fromManageUsers) { //RC-1457
              props.setShowDisabledUsers(showDisabledUsers!);
              //props.getTeachers(showDisabledUsers);
              handleHide(true);
            }
          },
          (error: string) => {
            toastr.error("Error", error);
          });
          toastr.success("Success", 'User details updated successfully.')
        });
      }
    }
  };

  const inviteIsAccepted = useMemo(() => {
    return selectedTeacher && selectedTeacher.invite
      ? selectedTeacher.invite.status === InviteStatuses.INVITE_ACCEPTED
      : false;
  }, [selectedTeacher]);

  const inviteIsSent = useMemo(() => {
    return selectedTeacher && selectedTeacher.invite
      ? selectedTeacher.invite.status === InviteStatuses.INVITE_SENT && !selectedTeacher.invite.is_expired
      : false;
  }, [selectedTeacher]);

  const inviteIsExpired = useMemo(() => {
    return selectedTeacher && selectedTeacher.invite
      ? (selectedTeacher.invite.status === InviteStatuses.INVITE_SENT) && selectedTeacher.invite.is_expired
      : false;
  }, [selectedTeacher]);

  // const infoByTargetRole = useMemo(() => {
  //   switch (selectedTeacher?.user.profile.current_assignment?.role) {
  //     case UserAssignmentRole.TEACHER:
  //     case UserAssignmentRole.NJTSS_IMPORTED_USER:
  //       return inviteIsExpired
  //               ? "You can update their information below if needed or resend the invite. In the meantime, you can roster students."
  //               : inviteIsSent 
  //                 ? "You can update their information below if needed or resend the invite. In the meantime, you can roster students."
  //                 : "You can update their information below if needed. In the meantime, you can roster students."
  //     case UserAssignmentRole.COACH:
  //       return inviteIsAccepted
  //             ? "You can update their information below if needed. In the meantime, you can assign educators."
  //             : inviteIsSent
  //             ? "You can update their information below if needed or resend the invite. In the meantime, you can assign educators."
  //             : inviteIsExpired
  //               ? "You can update their information below if needed or resend the invite. In the meantime, you can assign educators."
  //               : "You can update their information below if needed. In the meantime, you can assign educators.";
  //     case UserAssignmentRole.SUPERVISOR:
  //       return "In the meantime, you can assign educators below.";
  //     default:
  //       return "You can update their information below if needed. In the meantime, you can roster students and set up classes.";
  //   }
  // }, [selectedTeacher?.user.profile.current_assignment?.role]);

  const getTabByTargetRole = useMemo(() => {
    switch (selectedTeacher?.user.profile.current_assignment?.role) {
      case UserAssignmentRole.COACH:
      case UserAssignmentRole.SUPERVISOR:
        return <EducatorsManagementTab />;
      case UserAssignmentRole.TEACHER:
      default:
        return <TeacherTab />;
    }
  }, [selectedTeacher?.user.profile.current_assignment?.role]);

  const fullName = useMemo(() => {
    return getFullName(selectedTeacher?.user) ?? "User";
  }, [selectedTeacher?.user]);

  const { isHighLevelRole, isCoach } = useUserRole();

  // const youCanMessage = useMemo(() => {
  //   if (!selectedTeacher) {
  //     return null;
  //   }

  //   if (isHighLevelRole) {
  //     return `You can edit ${fullName}'s information
  //               below if it's incorrect.`;
  //   }

  //   return isCoach ? `This information is read-only.`
  //   : selectedTeacher.user.profile.is_active
  //     ? `You can't edit ${fullName}'s information. He/she able to manage it.`
  //     : `You can edit ${fullName}'s information
  //               below if it's incorrect. He/she will be able to manage it after
  //               accepting his/her invite.`;
  // }, [fullName, isHighLevelRole, selectedTeacher?.user.profile.is_active]);

  return selectedTeacher ? (
    <>
      <Modal
        animation={false}
        backdropClassName="customDarkModalBackdrop in"
        show={showModal}
        onHide={() => handleHide(false)}
        size="lg"
        centered
        dialogClassName="modal-dialog-no-scroll"
        // style={{margin: "1.1rem auto"}}
      >
        <Modal.Header
          closeButton
          className="purpleModalHeader orangeModalHeader"
        >
          <Modal.Title>{`Manage ${fullName}'s Details`}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
        {isHighLevelRole 
        ?
          <h4 className="mb-4"> Some details are read-only.
            {selectedTeacher?.user.profile.current_assignment?.role
            === UserAssignmentRole.COACH
            ? ' Educator assignments'
            : ' Student assignments'} take effect immediately. Editable fields require saving.
          </h4> 
        : 
        <div className="d-flex justify-content-center mb-4">
          <InfoButton id={"readonlyDetails"} icon={faLock} text={"Read-only"} /> 
          <h4 className="ml-2 align-self-center">
            All information is read-only for your account type.
          </h4>
        </div>
        }
          {/* <!-- Info Text --> */}
        {/* <h2 className="font-weight-bold">{`${
            inviteIsExpired
              ? `${fullName}'s invite has expired.`
              : inviteIsAccepted
                ? `${fullName} has accepted the invite.`
                : inviteIsSent 
                  ? `An invite has been sent to ${fullName} but hasn’t been accepted yet.` 
                  : `${fullName} hasn't been invited yet.`
          }`}</h2> */}
          {/* <h4>{youCanMessage}</h4> */}
          {/* <h4>{!isCoach ? infoByTargetRole : ''}</h4>
          <br /> */}
        {/* <p className=" small mb-4">
          Some details are read-only. User status changes and educator assignments
          take effect immediately. Editable fields require Save.
        </p> */}

        <div className="row">
          {/* <!-- LEFT COLUMN --> */}
          <div className="col-md-6">

            {/* <!-- User Details --> */}
            <h6 className="font-weight-bold mb-2">
              User Details {isHighLevelRole && <InfoButton id={"editableDetails"} icon={faLock} text={"Read-only"} />}
            </h6>

            <div className="border rounded p-3 mb-4">
              <div className="row mb-2">
                <div className="col-4 font-weight-bold pr-0"><label>Account Type:</label></div>
                <div className="col-8"><label>{UserAssignmentRoleDisplayedNames[selectedTeacher?.user.profile.current_assignment?.role!]}</label></div>
              </div>
              <div className="row mb-2">
                <div className="col-4 font-weight-bold pr-0"><label>Invite Status:</label></div>
                <div className="col-8"><label>{
                    inviteIsExpired
                      ?  'Expired'
                      : inviteIsAccepted
                        ? 'Accepted'
                        : inviteIsSent
                          ? 'Sent (Pending)'
                          : 'Not Invited'}
                </label></div>
              </div>
              {selectedTeacher?.user.profile.current_assignment?.role != UserAssignmentRole.NJTSS_IMPORTED_USER 
              && 
              <div className="row">
                <div className="col-4 font-weight-bold pr-0"><label>Email:</label></div>
                <div className="col-8">
                  <label className="text-wrap" style={{ wordBreak: "break-all", whiteSpace: "pre-line" }}>
                  {selectedTeacher?.user.email}
                  </label>
                </div>
              </div>}
            </div>

            {/* <!-- Editable Details --> */}
            <h6 className="font-weight-bold mb-2">
              Editable Details {isHighLevelRole && <InfoButton id={"editableDetails"} text={"Editable fields require saving."} />}
            </h6>

            <div className="border rounded p-3">
              <TeacherInfoForm
                handleSubmit={handleSubmit}
                isHighLevelRole={isHighLevelRole}
                isCoach={isCoach}
                fromManageUsers={fromManageUsers}
                onFieldValidated={setIsFieldValidated}
              />
            </div>

          </div>

          {/* <!-- RIGHT COLUMN --> */}
          <div className="col-md-6 border-left">
            {getTabByTargetRole}
          </div>
        </div>
        </Modal.Body>
        <Modal.Footer>
          {!isCoach && 
              <div className="btnActions px-3 mt-0">
                <button
                  onClick={() => handleHide(false)}
                  className="whiteBtnSm"
                >
                  Cancel
                </button>
                <button
                 className="blueBtnSm" 
                 type="submit" 
                 form="teacherInfoForm"
                 disabled={props.isLoading.updateTeacherInfo || !isFieldValidated}>
                  Save{" "}
                  {props.isLoading.updateTeacherInfo && (
                    <Spinner animation="border" size="sm" />
                  )}
                </button>

              </div>
            
            }
        </Modal.Footer>
      </Modal>
      <UploadStudentsRosterToTeacherModal />
      <CreateClassModal />
    </>
  ) : null;
};

const mapStateToProps = ({ onboarding }: ApplicationState): PropsFromState => {
  const selectedTeacher = onboarding.selectedTeacher;
  const assignStudentsToTeacherLoading = selectedTeacher
    ? onboarding.isLoading.assignStudentsToTeacher.includes(
        selectedTeacher.user.id
      )
    : false;
  let assignStudentsToTeacherError;
  if (selectedTeacher) {
    const error = onboarding.errors.assignStudentsToTeacher.find(
      (x) => x.teacherId === selectedTeacher.user.id
    );
    if (error) {
      assignStudentsToTeacherError = error.message;
    }
  }
  return {
    showModal: onboarding.modalsState.teacherDetailModal,
    selectedTeacher: selectedTeacher,
    teachersClasses: onboarding.teachersClasses,
    teachersStudents: onboarding.teachersStudents,
    isLoading: {
      assignClassToTeacher: onboarding.isLoading.assignClassToTeacher,
      updateTeacherInfo: onboarding.isLoading.updateTeacherInfo,
      updateTeacherClass: onboarding.isLoading.updateTeacherClass,
      assignStudentsToTeacher:
        assignStudentsToTeacherLoading ||
        onboarding.isLoading.updateStudentDetails,
    },
    errors: {
      assignClassToTeacher: onboarding.errors.assignClassToTeacher,
      getClassesAssignedToTeacher:
        onboarding.errors.getClassesAssignedToTeacher,
      getStudentsAssignedToTeacher:
        onboarding.errors.getStudentsAssignedToTeacher,
      updateTeacherInfo: onboarding.errors.updateTeacherInfo,
      updateTeacherClass: onboarding.errors.updateTeacherClass,
      assignStudentsToTeacher: assignStudentsToTeacherError,
    },
  };
};

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => {
  return bindActionCreators(
    {
      getClassesAssignedToTeacher: getClassesAssignedToTeacher,
      getStudentsAssignedToTeacher: getStudentsAssignedToTeacher,
      assignStudentsToTeacher: assignStudentsToTeacher,
      getTeachers: getTeachers,
      updateTeacher: updateTeacher,
      changeSelectedTeacher: changeSelectedTeacher,
      hideTeacherDetailsModal: hideTeacherDetailsModal,
      setShowDisabledUsers: setShowDisabledUsers,
      checkEmailExist: checkEmailExist,
      changeUserStatus: changeUserStatus,
    },
    dispatch
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(TeacherDetailsModal);
